<template>
  <div class="roles-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Roles
        </h1>
        <v-btn
          v-if="hasPermission('write:role')"
          color="primary"
          class="mank-btn"
          elevation="0"
          @click="openCreateDialog">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create
        </v-btn>
      </div>

      <div class="main-content">
        <div class="table-area table-area--pad">
          <div v-if="loading" class="d-flex justify-center py-12">
            <v-progress-circular indeterminate color="primary"/>
          </div>

          <div v-else-if="!roles.length" class="empty-state text-center">
            <div class="empty-state-icon mb-4">
              <v-icon size="80" color="grey-lighten-2">
                mdi-shield-account-outline
              </v-icon>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No Roles Found
              </h3>
              <p class="text-body-3 text--secondary">
                Create a new role by clicking the button above.
              </p>
            </div>
          </div>

          <v-data-table
            v-else
            :headers="headers"
            :items="roles"
            :loading="loading"
            class="mank-data-table"
            :items-per-page="-1"
            hide-default-footer
            no-data-text="No roles found"
            loading-text="Loading...">
            <template v-slot:item.name="{ item }">
              <span class="text-element-2 font-weight-medium">{{ item.name }}</span>
            </template>
            <template v-slot:item.description="{ item }">
              <span class="text-element-2 text--secondary">{{ item.description || '—' }}</span>
            </template>
            <template v-slot:item.is_system="{ item }">
              <v-chip
                v-if="item.is_system"
                x-small
                color="primary"
                class="system-badge">
                System
              </v-chip>
              <v-chip
                v-else
                x-small
                outlined
                class="custom-badge">
                Custom
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-end">
                <v-btn v-if="hasPermission('write:role')"
                       icon
                       small
                       class="mr-1"
                       @click="openEditDialog(item)"
                       :disabled="item.is_system">
                  <v-icon size="18">
                    mdi-pencil-outline
                  </v-icon>
                </v-btn>
                <v-btn v-if="hasPermission('write:role')"
                       icon
                       small
                       class="mr-1"
                       @click="openPermissionDialog(item)"
                       :disabled="item.is_system">
                  <v-icon size="18">
                    mdi-shield-edit-outline
                  </v-icon>
                </v-btn>
                <v-btn v-if="hasPermission('write:role')" icon small @click="confirmDelete(item)" :disabled="item.is_system">
                  <v-icon size="18" color="error">
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <!-- Edit Role Dialog -->
    <v-dialog v-model="editDialog" max-width="520" persistent>
      <v-card class="mank-dialog">
        <div class="dialog-title">
          Edit Role
        </div>
        <div class="dialog-body">
          <div class="form-field">
            <label class="field-label">Name</label>
            <MankTextField
              v-model="editForm.name"
              placeholder="e.g. editor"
              hide-details="auto"
              :error-messages="editErrors.name"/>
          </div>
          <div class="form-field">
            <label class="field-label">Description</label>
            <MankTextarea
              v-model="editForm.description"
              placeholder="Describe this role"
              hide-details="auto"
              rows="3"/>
          </div>
        </div>
        <div class="dialog-actions">
          <v-btn text class="mank-btn mank-btn--text" @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="editSaving"
            @click="saveRole">
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Create Role Dialog -->
    <v-dialog v-model="createDialog" max-width="520" persistent>
      <v-card class="mank-dialog">
        <div class="dialog-title">
          Create Role
        </div>
        <div class="dialog-body">
          <div class="form-field">
            <label class="field-label">Name</label>
            <MankTextField
              v-model="form.name"
              placeholder="e.g. editor"
              hide-details="auto"
              :error-messages="errors.name"/>
          </div>
          <div class="form-field">
            <label class="field-label">Description</label>
            <MankTextarea
              v-model="form.description"
              placeholder="Describe this role"
              hide-details="auto"
              rows="3"/>
          </div>
          <div class="form-field">
            <label class="field-label">Permissions</label>
            <div v-if="permissionsLoading" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate color="primary" size="24"/>
            </div>
            <div v-else class="permissions-list">
              <v-checkbox
                v-for="perm in allPermissions"
                :key="perm.id"
                v-model="form.permissions"
                :value="perm.id"
                :label="perm.name"
                hide-details
                class="mb-2"/>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <v-btn text class="mank-btn mank-btn--text" @click="closeCreateDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="saving"
            @click="createRole">
            Create
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Assign Permissions Dialog -->
    <v-dialog v-model="permissionDialog" max-width="520" persistent>
      <v-card class="mank-dialog">
        <div class="dialog-title">
          Assign Permissions
          <span class="role-name-label ml-2">— {{ selectedRole && selectedRole.name }}</span>
        </div>
        <div class="dialog-body">
          <div v-if="permissionsLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="primary"/>
          </div>
          <div v-else>
            <div class="permissions-list">
              <v-checkbox
                v-for="perm in allPermissions"
                :key="perm.id"
                v-model="selectedPermissions"
                :value="perm.id"
                :label="perm.name"
                hide-details
                class="mb-2"/>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <v-btn text class="mank-btn mank-btn--text" @click="permissionDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="assigningSaving"
            @click="assignPermissions">
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <DeleteConfirmationModal
      :visible="deleteDialog"
      :confirm-loading="deleteLoading"
      :detail="selectedRole ? `Role: ${selectedRole.name}` : ''"
      @confirm="deleteRole"
      @cancel="deleteDialog = false"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RoleService from '../services/RoleService';
import PermissionService from '../services/PermissionService';
import Icon from '../components/Icon.vue';
import MankTextField from '../components/MankTextField.vue';
import MankTextarea from '../components/MankTextarea.vue';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';

export default {
  name: 'Roles',
  components: { Icon, MankTextField, MankTextarea, DeleteConfirmationModal },
  data() {
    return {
      roles: [],
      allPermissions: [],
      loading: false,
      saving: false,
      assigningSaving: false,
      permissionsLoading: false,
      editDialog: false,
      editSaving: false,
      editForm: { name: '', description: '' },
      editErrors: { name: '' },
      createDialog: false,
      permissionDialog: false,
      deleteDialog: false,
      deleteLoading: false,
      selectedRole: null,
      selectedPermissions: [],
      form: { name: '', description: '', permissions: [] },
      errors: { name: '' },
      headers: [
        { text: 'NAME', value: 'name', sortable: true },
        { text: 'DESCRIPTION', value: 'description', sortable: false },
        { text: 'TYPE', value: 'is_system', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: { ...mapGetters('auth', ['hasPermission']) },
  mounted() {
    this.fetchRoles();
  },
  methods: {
    async fetchRoles() {
      if (!this.$store.getters['auth/isAuthenticated']) return;
      this.loading = true;
      try {
        this.roles = await RoleService.getAll();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load roles.' });
      } finally {
        this.loading = false;
      }
    },
    openEditDialog(role) {
      this.selectedRole = role;
      this.editForm = { name: role.name, description: role.description || '' };
      this.editErrors = { name: '' };
      this.editDialog = true;
    },
    async saveRole() {
      this.editErrors.name = '';
      if (!this.editForm.name.trim()) {
        this.editErrors.name = 'Name is required.';
        return;
      }
      this.editSaving = true;
      try {
        await RoleService.updateRole(this.selectedRole.id, this.editForm);
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'Role updated.' });
        this.editDialog = false;
        this.fetchRoles();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to update role.' });
      } finally {
        this.editSaving = false;
      }
    },
    async openCreateDialog() {
      this.form = { name: '', description: '', permissions: [] };
      this.errors = { name: '' };
      this.createDialog = true;
      this.permissionsLoading = true;
      try {
        this.allPermissions = await PermissionService.getAll();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load permissions.' });
      } finally {
        this.permissionsLoading = false;
      }
    },
    closeCreateDialog() {
      this.createDialog = false;
      this.form = { name: '', description: '', permissions: [] };
      this.errors = { name: '' };
    },
    async createRole() {
      this.errors.name = '';
      if (!this.form.name.trim()) {
        this.errors.name = 'Name is required.';
        return;
      }
      this.saving = true;
      try {
        const role = await RoleService.createRole({ name: this.form.name, description: this.form.description });
        if (this.form.permissions.length) {
          await RoleService.assignPermissionsToRole(role.id, this.form.permissions);
        }
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'Role created.' });
        this.closeCreateDialog();
        this.fetchRoles();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to create role.' });
      } finally {
        this.saving = false;
      }
    },
    async openPermissionDialog(role) {
      this.selectedRole = role;
      this.selectedPermissions = (role.permissions || []).map((p) => p.id || p);
      this.permissionDialog = true;
      this.permissionsLoading = true;
      try {
        this.allPermissions = await PermissionService.getAll();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load permissions.' });
      } finally {
        this.permissionsLoading = false;
      }
    },
    async assignPermissions() {
      this.assigningSaving = true;
      try {
        await RoleService.assignPermissionsToRole(this.selectedRole.id, this.selectedPermissions);
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'Permissions assigned.' });
        this.permissionDialog = false;
        this.fetchRoles();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to assign permissions.' });
      } finally {
        this.assigningSaving = false;
      }
    },
    confirmDelete(role) {
      this.selectedRole = role;
      this.deleteDialog = true;
    },
    async deleteRole() {
      this.deleteLoading = true;
      try {
        await RoleService.deleteRole(this.selectedRole.id);
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'Role deleted.' });
        this.deleteDialog = false;
        this.fetchRoles();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to delete role.' });
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.roles-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-area {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 32px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: $light-foreground-1;
}

.main-content {
  flex: 1;
}

.table-area {
  background: $light-surface-1;
  border-radius: 12px;
  border: 1px solid $light-border;

  &--pad {
    padding: 24px;
  }
}

.empty-state {
  padding: 60px 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  padding: 24px 24px 16px;
  display: flex;
  align-items: center;
}

.dialog-body {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: $light-foreground-1;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
}

.role-name-label {
  font-weight: 400;
  font-size: 16px;
  color: $light-foreground-3;
}

.dialog-actions {
  padding: 16px 24px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.permissions-list {
  max-height: 300px;
  overflow-y: auto;
}

.permission-chip {
  font-size: 11px;
}

.system-badge {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.custom-badge {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
